import React, { useState } from "react";
import { Loader2, Sparkles, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { db, AI_MODELS } from "@/api/base44Client";
import { PROFESSIONS, professionSubjects, getSubject } from "@/lib/subjects";
import { ProfessionSubjectSelect } from "./ProfessionSubjectSelect";

const firstProf = PROFESSIONS[0].id;
const firstSubj = (p) => professionSubjects(p)[0]?.id || "";

export function AiGeneratorPanel({ onGenerated }) {
  const [jobs, setJobs] = useState([{ id: 1, prof: firstProf, subject: firstSubj(firstProf) }]);
  const [count, setCount] = useState(10);
  const [model, setModel] = useState(AI_MODELS[0].id);
  const [topic, setTopic] = useState("");
  const [withImages, setWithImages] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState([]);

  const addJob = () => setJobs((j) => [...j, { id: Date.now(), prof: firstProf, subject: firstSubj(firstProf) }]);
  const removeJob = (id) => setJobs((j) => j.filter((x) => x.id !== id));
  const updateJob = (id, prof, subject) => setJobs((j) => j.map((x) => x.id === id ? { ...x, prof, subject } : x));

  const generate = async () => {
    setBusy(true);
    setProgress(jobs.map((j) => ({ ...j, status: "wartend", created: 0 })));
    let totalCreated = 0;
    for (let i = 0; i < jobs.length; i++) {
      const job = jobs[i];
      const subName = getSubject(job.subject)?.name || job.subject;
      setProgress((p) => p.map((x, xi) => xi === i ? { ...x, status: "läuft…" } : x));
      try {
        const r = await db.admin.generateQuestions({
          profession: job.prof, subject: job.subject,
          subject_name: subName, count, model,
          topic: topic || null, with_images: withImages,
        });
        totalCreated += r.created || 0;
        setProgress((p) => p.map((x, xi) => xi === i ? { ...x, status: `✓ ${r.created} Fragen`, created: r.created } : x));
        toast.success(`${r.created} Fragen für „${subName}" erstellt`);
      } catch (e) {
        setProgress((p) => p.map((x, xi) => xi === i ? { ...x, status: "✗ Fehler" } : x));
        toast.error(`Fehler bei „${subName}": ` + (e?.response?.data?.detail || e?.message || "unbekannt"));
      }
    }
    setBusy(false);
    if (totalCreated > 0) { onGenerated(); }
  };

  return (
    <section className="adm-card" data-testid="admin-ai-generator">
      <div className="adm-card-head">
        <h2 className="adm-card-title"><span className="adm-card-icon"><Sparkles className="h-4 w-4" /></span>KI-Fragen-Generator</h2>
        <span className="adm-muted">Mehrere Fächer nacheinander generieren</span>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", marginBottom: "1rem" }}>
        {jobs.map((job, i) => (
          <div key={job.id} style={{ display: "flex", alignItems: "flex-end", gap: "0.5rem", background: "var(--adm-row-bg, #f8fafc)", borderRadius: "8px", padding: "0.75rem" }}>
            <span style={{ fontSize: "12px", fontWeight: 700, minWidth: "1.5rem" }}>{i + 1}.</span>
            <div style={{ flex: 1 }}>
              <ProfessionSubjectSelect
                profession={job.prof} subject={job.subject}
                onChange={(p, s) => updateJob(job.id, p, s)}
                testPrefix={`admin-ai-${job.id}`}
              />
            </div>
            {progress[i] && (
              <span style={{ fontSize: "12px", fontWeight: 700, minWidth: "80px", color: progress[i].status?.startsWith("✓") ? "#16a34a" : progress[i].status?.startsWith("✗") ? "#dc2626" : "#64748b" }}>
                {progress[i].status}
              </span>
            )}
            {jobs.length > 1 && (
              <button onClick={() => removeJob(job.id)} disabled={busy} style={{ padding: "0.25rem", border: "none", background: "none", cursor: "pointer", opacity: 0.6 }}>
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
        <button className="adm-btn adm-btn-sm" onClick={addJob} disabled={busy} style={{ alignSelf: "flex-start" }}>
          <Plus className="h-3.5 w-3.5" /> Fach hinzufügen
        </button>
      </div>

      <div className="adm-grid adm-grid-3">
        <label className="adm-field"><span className="adm-label">Anzahl Fragen pro Fach</span>
          <select className="adm-input" value={count} onChange={(e) => setCount(Number(e.target.value))}>
            {[5, 10, 25, 50].map((n) => <option key={n} value={n}>{n} Fragen</option>)}
          </select>
        </label>
        <label className="adm-field"><span className="adm-label">KI-Modell</span>
          <select className="adm-input" value={model} onChange={(e) => setModel(e.target.value)}>
            {AI_MODELS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
        </label>
        <label className="adm-field" style={{ gridColumn: "span 1" }}><span className="adm-label">Thema (optional)</span>
          <input className="adm-input" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="z.B. Bilanz, Mehrwertsteuer…" />
        </label>
      </div>

      <label className="adm-inline-check" style={{ marginTop: "0.75rem" }}>
        <input type="checkbox" className="adm-check" checked={withImages} onChange={(e) => setWithImages(e.target.checked)} />
        <span><strong>Mit Bildfragen</strong> – die KI zeichnet passende Grafiken</span>
      </label>

      <button className="adm-btn adm-btn-block" disabled={busy || jobs.every((j) => !j.subject)} onClick={generate} style={{ marginTop: "1rem" }}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
        {busy ? `Generiert Fach ${progress.findIndex((p) => p.status === "läuft…") + 1} von ${jobs.length}…` : `${jobs.length} Fach${jobs.length > 1 ? "\\u00e4cher" : ""} generieren`}
      </button>
    </section>
  );
}
